import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Users, BookOpen, PhoneCall, PlusCircle, 
  Check, X, Clock, Download, Search, Trash2, 
  UserCheck, Calendar, Filter, ArrowUpRight, ShieldCheck, Eye, RotateCcw
} from 'lucide-react';
import { 
  getAllReports, 
  clearAllReports,
  getEmployees, 
  addEmployee, 
  deleteEmployee, 
  deleteReport,
  exportReportsToCsv, 
  formatDate 
} from '../../lib/reportService';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reports'); // default to work reports for convenience
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    inquiries: [],
    students: [],
    followUps: [],
    courses: []
  });

  // Work Reports State
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState('ALL');
  const [selectedDateFilter, setSelectedDateFilter] = useState('');
  const [viewReportModal, setViewReportModal] = useState(null);

  // Add Employee Form State
  const [showAddEmpModal, setShowAddEmpModal] = useState(false);
  const [newEmpForm, setNewEmpForm] = useState({ id: '', name: '', role: 'Intern', pin: '1234' });
  const [empFeedback, setEmpFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    // Check authentication
    if (!localStorage.getItem('adminAuthenticated')) {
      navigate('/admin');
      return;
    }
    
    // Fetch data
    fetchData();
    refreshReportData();
  }, [navigate]);

  const refreshReportData = () => {
    setReports(getAllReports());
    setEmployees(getEmployees());
  };

  const fetchData = async () => {
    try {
      const res = await fetch('http://localhost:8000/api/inquiry');
      if (res.ok) {
        const inquiries = await res.json();
        setData((prev) => ({ ...prev, inquiries }));
      }
      setLoading(false);
    } catch {
      // Backend may not be running locally, fallback gracefully
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    navigate('/admin');
  };

  // Add Employee Handler
  const handleAddEmployee = (e) => {
    e.preventDefault();
    setEmpFeedback({ type: '', message: '' });
    try {
      addEmployee(newEmpForm);
      setEmpFeedback({ type: 'success', message: `Added employee ${newEmpForm.name} (${newEmpForm.id}) successfully!` });
      setNewEmpForm({ id: '', name: '', role: 'Intern', pin: '1234' });
      refreshReportData();
      setTimeout(() => {
        setShowAddEmpModal(false);
        setEmpFeedback({ type: '', message: '' });
      }, 1500);
    } catch (err) {
      setEmpFeedback({ type: 'error', message: err.message });
    }
  };

  // Delete Employee Handler
  const handleDeleteEmployee = (empId) => {
    if (window.confirm(`Are you sure you want to remove employee ${empId}?`)) {
      deleteEmployee(empId);
      refreshReportData();
    }
  };

  // Delete Report Handler
  const handleDeleteReport = (reportId) => {
    if (window.confirm('Are you sure you want to delete this report entry?')) {
      deleteReport(reportId);
      refreshReportData();
    }
  };

  // Reset All Reports Handler
  const handleResetAllReports = () => {
    if (window.confirm('Are you sure you want to reset and clear ALL work reports? This will reset submissions, total hours, and stats to 0.')) {
      clearAllReports();
      refreshReportData();
    }
  };

  // Filtered reports for Admin
  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      const matchesEmp = selectedEmployeeFilter === 'ALL' || r.employeeId === selectedEmployeeFilter;
      const matchesDate = !selectedDateFilter || r.date === selectedDateFilter;
      const matchesSearch = !searchTerm || 
        r.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.tasksDone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.role?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesEmp && matchesDate && matchesSearch;
    });
  }, [reports, selectedEmployeeFilter, selectedDateFilter, searchTerm]);

  // Aggregate Metrics for Admin
  const totalHoursLogged = useMemo(() => {
    return filteredReports.reduce((sum, r) => sum + (parseFloat(r.hoursWorked) || 0), 0);
  }, [filteredReports]);

  const uniqueActiveWorkers = useMemo(() => {
    const ids = new Set(filteredReports.map(r => r.employeeId));
    return ids.size;
  }, [filteredReports]);

  // -------------------------------------------------------------
  // WORK REPORTS TAB CONTENT
  // -------------------------------------------------------------
  const WorkReportsList = () => (
    <div className="space-y-6">
      {/* Metric Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-xl p-5">
          <span className="text-xs uppercase text-gray-400 font-semibold tracking-wider">Total Hours</span>
          <div className="text-2xl font-bold font-mono text-orange-400 mt-1">{totalHoursLogged} hrs</div>
          <span className="text-[11px] text-gray-500 mt-1 block">Across all filtered logs</span>
        </div>

        <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-xl p-5">
          <span className="text-xs uppercase text-gray-400 font-semibold tracking-wider">Total Submissions</span>
          <div className="text-2xl font-bold font-mono text-white mt-1">{filteredReports.length}</div>
          <span className="text-[11px] text-gray-500 mt-1 block">Daily work reports</span>
        </div>

        <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-xl p-5">
          <span className="text-xs uppercase text-gray-400 font-semibold tracking-wider">Active Employees</span>
          <div className="text-2xl font-bold font-mono text-emerald-400 mt-1">{uniqueActiveWorkers} / {employees.length}</div>
          <span className="text-[11px] text-gray-500 mt-1 block">Reporting contributors</span>
        </div>

        <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-xl p-5">
          <span className="text-xs uppercase text-gray-400 font-semibold tracking-wider">Daily Avg / Entry</span>
          <div className="text-2xl font-bold font-mono text-white mt-1">
            {filteredReports.length > 0 ? (totalHoursLogged / filteredReports.length).toFixed(1) : 0} hrs
          </div>
          <span className="text-[11px] text-gray-500 mt-1 block">Per daily record</span>
        </div>
      </div>

      {/* Action and Filter Controls Bar */}
      <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2 text-xs text-gray-400 font-medium">
            <Filter size={14} className="text-orange-500" />
            <span>Employee:</span>
          </div>
          <select
            value={selectedEmployeeFilter}
            onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
            className="bg-black/60 border border-orange-500/30 text-white text-xs rounded-lg px-3 py-2 focus:border-orange-500 focus:outline-none"
          >
            <option value="ALL">All Employees ({employees.length})</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.name} ({emp.id})
              </option>
            ))}
          </select>

          <div className="flex items-center space-x-2 text-xs text-gray-400 font-medium ml-2">
            <Calendar size={14} className="text-orange-500" />
            <span>Date:</span>
          </div>
          <input
            type="date"
            value={selectedDateFilter}
            onChange={(e) => setSelectedDateFilter(e.target.value)}
            className="bg-black/60 border border-orange-500/30 text-white text-xs rounded-lg px-3 py-1.5 focus:border-orange-500 focus:outline-none"
          />
          {selectedDateFilter && (
            <button
              onClick={() => setSelectedDateFilter('')}
              className="text-xs text-orange-400 hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleResetAllReports}
            className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500 hover:text-white font-semibold px-3 py-2 rounded-lg flex items-center space-x-1.5 text-xs transition-colors"
            title="Reset and clear all work reports"
          >
            <RotateCcw size={14} />
            <span>Reset Reports</span>
          </button>
          <button
            onClick={() => exportReportsToCsv(filteredReports)}
            className="bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500 hover:text-black font-semibold px-4 py-2 rounded-lg flex items-center space-x-2 text-xs transition-colors"
          >
            <Download size={14} />
            <span>Export to CSV</span>
          </button>
          <a
            href="/skill/report"
            target="_blank"
            rel="noreferrer"
            className="bg-gray-800 text-gray-300 hover:text-white px-3 py-2 rounded-lg text-xs flex items-center space-x-1.5 transition-colors border border-gray-700"
          >
            <span>Open Employee Portal</span>
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-gray-900/50 rounded-xl border border-orange-500/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-black/50 border-b border-orange-500/20 text-xs uppercase text-gray-400">
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Employee</th>
                <th className="py-3 px-4">Hours</th>
                <th className="py-3 px-4">Timezone</th>
                <th className="py-3 px-4">Accomplishments & Comments</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/60">
              {filteredReports.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-gray-400 py-12">
                    <Clock size={32} className="mx-auto mb-2 text-orange-500 opacity-40" />
                    <p className="text-sm font-medium text-gray-300">No work reports submitted yet.</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Ready for a fresh start! Employees can submit daily reports at <span className="text-orange-400">/skill/report</span>.
                    </p>
                  </td>
                </tr>
              ) : (
                filteredReports.map(rep => (
                  <tr key={rep.id} className="hover:bg-black/20 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-medium text-white whitespace-nowrap">
                      {rep.date}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="font-semibold text-white">{rep.employeeName}</div>
                      <div className="text-xs text-orange-400 font-mono">{rep.employeeId} • {rep.role}</div>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-orange-400 font-bold whitespace-nowrap">
                      {rep.hoursWorked}h
                    </td>
                    <td className="py-3.5 px-4 text-xs text-gray-400 font-mono whitespace-nowrap">
                      {rep.timezone || 'Asia/Kolkata'}
                    </td>
                    <td className="py-3.5 px-4 max-w-md">
                      <p className="text-gray-200 line-clamp-2 text-xs leading-relaxed">
                        {rep.tasksDone}
                      </p>
                      {rep.notes && (
                        <p className="text-[11px] text-gray-400 italic mt-0.5 truncate">
                          Note: {rep.notes}
                        </p>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setViewReportModal(rep)}
                          className="p-1.5 text-gray-400 hover:text-orange-400 rounded hover:bg-gray-800 transition-colors"
                          title="View Full Report"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => handleDeleteReport(rep.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 rounded hover:bg-gray-800 transition-colors"
                          title="Delete Report"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // -------------------------------------------------------------
  // EMPLOYEES & INTERNS DIRECTORY TAB
  // -------------------------------------------------------------
  const EmployeesList = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-white futuristic-text">Interns & Employees Directory</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Manage authorized staff who can log work hours at <code className="text-orange-400">/skill/report</code>.
          </p>
        </div>
        <button
          onClick={() => setShowAddEmpModal(true)}
          className="bg-orange-500 text-black font-bold px-4 py-2 rounded-lg flex items-center space-x-2 text-xs hover:bg-orange-600 transition-colors"
        >
          <PlusCircle size={15} />
          <span>Add Employee / Intern</span>
        </button>
      </div>

      <div className="bg-gray-900/50 rounded-xl border border-orange-500/20 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-black/50 border-b border-orange-500/20 text-xs uppercase text-gray-400">
              <th className="py-3 px-4">Employee ID</th>
              <th className="py-3 px-4">Full Name</th>
              <th className="py-3 px-4">Role / Department</th>
              <th className="py-3 px-4">Login PIN</th>
              <th className="py-3 px-4">Total Logged Hours</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/60">
            {employees.map(emp => {
              const empReports = reports.filter(r => r.employeeId === emp.id);
              const totalHours = empReports.reduce((s, r) => s + (parseFloat(r.hoursWorked) || 0), 0);
              return (
                <tr key={emp.id} className="hover:bg-black/20">
                  <td className="py-3.5 px-4 font-mono font-bold text-orange-400">{emp.id}</td>
                  <td className="py-3.5 px-4 font-semibold text-white">{emp.name}</td>
                  <td className="py-3.5 px-4 text-gray-300">{emp.role}</td>
                  <td className="py-3.5 px-4 font-mono text-gray-400 text-xs">
                    <span className="bg-black/60 px-2 py-1 rounded border border-gray-800 tracking-widest">{emp.pin}</span>
                  </td>
                  <td className="py-3.5 px-4 font-mono text-white font-medium">
                    {totalHours} hrs ({empReports.length} days)
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      onClick={() => handleDeleteEmployee(emp.id)}
                      className="text-gray-400 hover:text-red-400 p-1.5 rounded hover:bg-gray-800 transition-colors"
                      title="Remove Employee"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Existing Inquiries List
  const InquiriesList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-white">Recent Inquiries</h3>
        <button className="bg-orange-500/20 text-orange-500 px-4 py-2 rounded-lg flex items-center space-x-2 text-xs">
          <Download size={14} />
          <span>Export</span>
        </button>
      </div>
      <div className="bg-gray-900/50 rounded-xl border border-orange-500/20 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-black/30 text-xs uppercase text-gray-300">
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Program</th>
              <th className="px-4 py-3 text-left">Message</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.inquiries.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center text-gray-400 py-6 text-sm">No inquiries found.</td>
              </tr>
            ) : (
              data.inquiries.map((inq, idx) => (
                <tr key={inq._id || idx} className="border-b border-orange-500/10 hover:bg-black/10 text-sm">
                  <td className="px-4 py-3 text-white">{inq.name}</td>
                  <td className="px-4 py-3 text-white">{inq.email}</td>
                  <td className="px-4 py-3 text-white">{inq.program}</td>
                  <td className="px-4 py-3 text-white">{inq.message}</td>
                  <td className="px-4 py-3 text-white">-</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const StudentsList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-white">Enrolled Students</h3>
        <div className="flex space-x-2">
          <button className="bg-orange-500/20 text-orange-500 px-4 py-2 rounded-lg flex items-center space-x-2 text-xs">
            <PlusCircle size={14} />
            <span>Add Student</span>
          </button>
          <button className="bg-orange-500/20 text-orange-500 px-4 py-2 rounded-lg flex items-center space-x-2 text-xs">
            <Download size={14} />
            <span>Export</span>
          </button>
        </div>
      </div>
    </div>
  );

  const FollowUpsList = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-white">Follow-ups</h3>
    </div>
  );

  const CoursesList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-white">Courses</h3>
        <button className="bg-orange-500 text-black px-4 py-2 rounded-lg flex items-center space-x-2 text-xs font-bold">
          <PlusCircle size={14} />
          <span>Add New Course</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white relative">
      {/* Background glow & grid */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>

      {/* Header */}
      <header className="bg-gray-900/50 border-b border-orange-500/20 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-white futuristic-text">InSkill Admin</h1>
              <span className="text-xs bg-orange-500/10 border border-orange-500/30 text-orange-400 px-2 py-0.5 rounded font-mono">
                Management Console
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="/skill/report"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-orange-400 hover:text-orange-300 font-medium flex items-center space-x-1"
              >
                <span>Employee Report View</span>
                <ArrowUpRight size={13} />
              </a>
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-red-400 transition-colors text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-800 hover:border-red-500/30"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search reports by employee, tasks, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-900/50 border border-orange-500/20 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>

          <Tabs defaultValue="reports" className="w-full">
            <TabsList className="bg-gray-900/50 p-1.5 rounded-xl border border-orange-500/20 flex flex-wrap gap-1">
              <TabsTrigger value="reports" onClick={() => setActiveTab('reports')}>
                <Clock size={16} className="mr-2 text-orange-500" />
                Work Reports
              </TabsTrigger>
              <TabsTrigger value="employees" onClick={() => setActiveTab('employees')}>
                <UserCheck size={16} className="mr-2 text-orange-500" />
                Employees & Interns
              </TabsTrigger>
              <TabsTrigger value="inquiries" onClick={() => setActiveTab('inquiries')}>
                <Users size={16} className="mr-2" />
                Inquiries
              </TabsTrigger>
              <TabsTrigger value="students" onClick={() => setActiveTab('students')}>
                <BookOpen size={16} className="mr-2" />
                Students
              </TabsTrigger>
              <TabsTrigger value="followups" onClick={() => setActiveTab('followups')}>
                <PhoneCall size={16} className="mr-2" />
                Follow-ups
              </TabsTrigger>
              <TabsTrigger value="courses" onClick={() => setActiveTab('courses')}>
                <BookOpen size={16} className="mr-2" />
                Courses
              </TabsTrigger>
            </TabsList>

            <div className="mt-6">
              <TabsContent value="reports">
                <WorkReportsList />
              </TabsContent>
              <TabsContent value="employees">
                <EmployeesList />
              </TabsContent>
              <TabsContent value="inquiries">
                <InquiriesList />
              </TabsContent>
              <TabsContent value="students">
                <StudentsList />
              </TabsContent>
              <TabsContent value="followups">
                <FollowUpsList />
              </TabsContent>
              <TabsContent value="courses">
                <CoursesList />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </main>

      {/* Modal: View Full Report Details */}
      {viewReportModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-orange-500/40 rounded-2xl p-6 max-w-lg w-full shadow-2xl relative">
            <button
              onClick={() => setViewReportModal(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>

            <div className="mb-4">
              <span className="text-xs uppercase font-mono text-orange-400 block">{viewReportModal.employeeId}</span>
              <h3 className="text-xl font-bold text-white">{viewReportModal.employeeName}</h3>
              <p className="text-xs text-gray-400">{viewReportModal.role}</p>
            </div>

            <div className="grid grid-cols-3 gap-2 bg-black/50 p-3 rounded-xl border border-gray-800 text-xs mb-4">
              <div>
                <span className="text-gray-500 block">Date</span>
                <span className="font-mono text-white font-semibold">{viewReportModal.date}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Hours</span>
                <span className="font-mono text-orange-400 font-bold">{viewReportModal.hoursWorked} hrs</span>
              </div>
              <div>
                <span className="text-gray-500 block">Timezone</span>
                <span className="font-mono text-white truncate block">{viewReportModal.timezone}</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h4 className="text-xs uppercase font-semibold text-gray-400 mb-1">Accomplishments</h4>
                <div className="bg-black/60 p-3 rounded-lg border border-gray-800/80 text-sm text-gray-200 whitespace-pre-line leading-relaxed max-h-48 overflow-y-auto">
                  {viewReportModal.tasksDone}
                </div>
              </div>

              {viewReportModal.notes && (
                <div>
                  <h4 className="text-xs uppercase font-semibold text-gray-400 mb-1">Notes & Blockers</h4>
                  <div className="bg-black/60 p-3 rounded-lg border border-gray-800/80 text-xs text-gray-300 italic">
                    {viewReportModal.notes}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setViewReportModal(null)}
                className="bg-orange-500 text-black font-bold px-4 py-2 rounded-lg text-xs hover:bg-orange-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Employee */}
      {showAddEmpModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-orange-500/40 rounded-2xl p-6 max-w-md w-full shadow-2xl relative">
            <button
              onClick={() => {
                setShowAddEmpModal(false);
                setEmpFeedback({ type: '', message: '' });
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>

            <h3 className="text-lg font-bold text-white mb-1 futuristic-text">Add New Employee / Intern</h3>
            <p className="text-xs text-gray-400 mb-4">
              Add credentials to authorize reporting at <code className="text-orange-400">/skill/report</code>.
            </p>

            {empFeedback.message && (
              <div className={`p-3 rounded-lg text-xs mb-4 ${
                empFeedback.type === 'success' 
                  ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
                  : 'bg-red-500/10 border border-red-500/30 text-red-400'
              }`}>
                {empFeedback.message}
              </div>
            )}

            <form onSubmit={handleAddEmployee} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-xs font-semibold uppercase mb-1">Employee ID</label>
                <input
                  type="text"
                  placeholder="e.g. INSK-006"
                  value={newEmpForm.id}
                  onChange={(e) => setNewEmpForm({ ...newEmpForm, id: e.target.value })}
                  required
                  className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none uppercase font-mono"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-xs font-semibold uppercase mb-1">Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. John Doe"
                  value={newEmpForm.name}
                  onChange={(e) => setNewEmpForm({ ...newEmpForm, name: e.target.value })}
                  required
                  className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-xs font-semibold uppercase mb-1">Role / Track</label>
                <input
                  type="text"
                  placeholder="e.g. Full Stack Intern, AI Intern"
                  value={newEmpForm.role}
                  onChange={(e) => setNewEmpForm({ ...newEmpForm, role: e.target.value })}
                  required
                  className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-xs font-semibold uppercase mb-1">Login PIN / Password</label>
                <input
                  type="text"
                  placeholder="e.g. 1234"
                  value={newEmpForm.pin}
                  onChange={(e) => setNewEmpForm({ ...newEmpForm, pin: e.target.value })}
                  required
                  className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none font-mono"
                />
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddEmpModal(false)}
                  className="bg-gray-800 text-gray-300 px-4 py-2 rounded-lg text-xs hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-orange-500 text-black font-bold px-4 py-2 rounded-lg text-xs hover:bg-orange-600 transition-colors"
                >
                  Save Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
